import { createSlice, PayloadAction, createAsyncThunk, ActionReducerMapBuilder } from "@reduxjs/toolkit";
import aiStudioService from "@apis/aiStudioService";
import report from "@report";
import { Status } from "@models/common";
import message from "@utils/message";
import { t } from "i18next";
import { videoGenerateStatus, VideoGenerateStatusRequest } from "@apis/http/api";
import type { components } from "@apis/generated/openapi";

type ProductSellingPoint = components["schemas"]["ProductSellingPoint"];
type CheckedSellingPoint = ProductSellingPoint & { checked: boolean };

export interface ProductAttribute {
  [key: string]: any;
}

export interface VideoParamsUserInput {
  videoId: string;
  videoSource: string;
  product_id: string;
  product_title: string;
  category: string;
  selling_points: string[];
  target_age: string;
  target_gender: string;
  imitation_level: string;
  language: string;
}

export interface ScriptRewriteState {
  // URL参数
  videoId: string;
  videoSource: string;
  country: string;

  // 状态
  status: Status;
  error: any;
  sellingPoints: { status: Status; data: CheckedSellingPoint[]; error?: any };
  productList: { status: Status; data: any[]; error?: any };
  productItem: { status: Status; data: any; error?: any };
  videoDetail: { status: Status; data: any; error?: any };
  rewriteScriptList: any[];
  tempParams: any;
  isTranslateLoading: boolean;
  hasProduct: boolean;
  target_age: string;
  target_gender: string;
  imitation_level: string;
  language: string;
  selling_points: string[];
}

const initialState: ScriptRewriteState = {
  videoId: "",
  videoSource: "",
  country: "",
  status: "idle",
  error: null,
  // 选中的视频详情
  videoDetail: { status: "idle", data: null },
  // 选中的商品详情
  productItem: { status: "idle", data: null },
  // 搜索到的商品列表
  productList: { status: "idle", data: [] },
  // 卖点列表
  sellingPoints: { status: "idle", data: [] },
  // 生成脚本列表
  rewriteScriptList: [],
  tempParams: {},
  isTranslateLoading: false,
  hasProduct: false,

  // 生成用参数
  target_age: "all",
  target_gender: "all",
  imitation_level: "standard",
  language: "auto",
  // 卖点文本列表
  selling_points: [],
};

export const fetchVideoDetail = createAsyncThunk(
  "scriptRewrite/fetchVideoDetail",
  async (reqData: { id: string; country: string; videoSource: string }) => {
    return await aiStudioService.fetchVideoDetail(reqData);
  },
);

export const fetchVideoLinkPreview = createAsyncThunk(
  "scriptRewrite/fetchVideoLinkPreview",
  async (reqData: { videoLink: string }) => {
    return await aiStudioService.fetchVideoLinkPreview(reqData);
  },
);

export const fetchProductInfo = createAsyncThunk(
  "scriptRewrite/fetchProductInfo",
  async (reqData: { videoId: string; country: string; videoSource: string }) => {
    return await aiStudioService.fetchProductInfo(reqData);
  },
);

export const fetchSellingPointsTask = createAsyncThunk(
  "scriptRewrite/fetchSellingPointsTask",
  async (reqData: { id: string; country: string }): Promise<number> => {
    return await aiStudioService.fetchSellingPointsTask(reqData);
  },
);

export const fetchSellingPointsResult = createAsyncThunk<
  ProductSellingPoint[],
  { id: string; attributeKey?: string; country: string }
>("scriptRewrite/fetchSellingPointsResult", async (reqData: { id: string; attributeKey?: string; country: string }) => {
  return await aiStudioService.fetchSellingPointsResult<ProductSellingPoint[]>(reqData);
});

export const productSearch = createAsyncThunk(
  "scriptRewrite/productSearch",
  async (reqData: { keyword: string; country: string }) => {
    return await aiStudioService.fetchProductSearch({ ...reqData, country: reqData.country || "US" });
  },
);

export const productSearchByUrl = createAsyncThunk(
  "scriptRewrite/productSearchByUrl",
  async (reqData: { keyword: string; country?: string }) => {
    return await aiStudioService.fetchProductSearch(reqData);
  },
);

export const fetchScriptGenerationResult = createAsyncThunk(
  "scriptRewrite/fetchScriptGenerationResult",
  async (reqData: { id: string; requestId: string; userInput: any; translate?: boolean }) => {
    const { userInput: _, ...apiParams } = reqData;
    return await aiStudioService.fetchScriptGenerationResult({ ...apiParams, translate: false });
  },
);

export const fetchScriptGenerationTask = createAsyncThunk(
  "scriptRewrite/fetchScriptGenerationTask",
  async (reqData: VideoGenerateStatusRequest) => {
    return await videoGenerateStatus(reqData);
  },
);

export const fetchScriptGenerationResultTranslate = createAsyncThunk(
  "scriptRewrite/fetchScriptGenerationResultTranslate",
  async (reqData: { requestId: string; activeScript: number; scriptIndex: number; translate?: boolean }) => {
    const { scriptIndex: _, ...apiParams } = reqData;
    return await aiStudioService.fetchScriptGenerationResult({ ...apiParams, translate: true });
  },
);

export const scriptRewriteSlice = createSlice({
  name: "scriptRewrite",
  initialState,
  reducers: {
    resetToInit: (state) => {
      Object.keys(initialState).forEach((key) => {
        (state as any)[key] = (initialState as any)[key];
      });
    },
    updateReady: (
      state,
      action: PayloadAction<{
        videoId: string;
        videoSource: string;
        country: string;
      }>,
    ) => {
      state.videoId = action.payload.videoId;
      state.videoSource = action.payload.videoSource;
      state.country = action.payload.country;
    },
    updateParams: (state, action: any) => {
      Object.entries(action.payload).forEach(([key, value]) => {
        if (key in state) {
          // @ts-expect-error: Dynamic assignment based on defined state keys
          state[key] = value;
        }
      });
    },
    updateVideo: (state, action: PayloadAction<{ videoDetail?: any; videoId: string; videoSource?: string }>) => {
      state.videoDetail = { data: action.payload.videoDetail || null, status: "succeeded" };
      state.videoId = action.payload.videoId;
      state.videoSource = action.payload.videoSource || "";
      state.country = action.payload.videoDetail?.country;
      state.hasProduct = action.payload.videoDetail?.hasProduct || false;
    },
    updateCountry: (state, action: PayloadAction<string>) => {
      state.country = action.payload;
    },
    handleSave: (state, action: PayloadAction<{ activeScript: number; [key: string]: any }>) => {
      state.rewriteScriptList = state.rewriteScriptList.map((item: any, index: number) => {
        if (index + 1 === action.payload.activeScript) {
          return {
            ...item,
            ...action.payload,
          };
        }
        return item;
      });
    },
    updateProductItem: (state, action) => {
      state.productItem = { data: action.payload, status: "succeeded" };
    },
    removeProductItem: (state) => {
      state.productItem = { data: null, status: "idle" };
      state.sellingPoints = { data: [], status: "idle" };
      state.productList = { data: [], status: "idle" };
      state.selling_points = [];
    },
    updateStatus: (state, action: PayloadAction<Status>) => {
      state.status = action.payload;
    },
    updateError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    updateTranslateStatus: (state, action: PayloadAction<{ activeScript: number; translate: boolean }>) => {
      state.rewriteScriptList = state.rewriteScriptList.map((item: any, index: number) => {
        return {
          ...item,
          translate: index === action.payload.activeScript ? action.payload.translate : item.translate,
        };
      });
    },
    removeSellingPoints: (state) => {
      state.sellingPoints = { data: [], status: "succeeded" };
      state.selling_points = [];
    },
    updateProductList: (state, action: PayloadAction<{ status: Status; data: any[] }>) => {
      state.productList = action.payload;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<ScriptRewriteState>) => {
    builder
      .addCase(fetchVideoDetail.pending, (state) => {
        state.videoDetail = { data: null, status: "loading" };
      })
      .addCase(fetchVideoDetail.fulfilled, (state, action: any) => {
        state.videoDetail = { data: action.payload, status: "succeeded" };
        state.videoId = action.payload?.id || "";
        state.videoSource = action.payload?.videoSource || "";
        state.hasProduct = action.payload?.hasProduct || false;
      })
      .addCase(fetchVideoDetail.rejected, (state, action) => {
        state.videoDetail = { data: null, status: "failed", error: action.error?.message };
        message.error(action.error?.message || t("Video not found, Please re-enter it."));
      });
    builder
      .addCase(fetchVideoLinkPreview.pending, (state) => {
        state.videoDetail = { data: null, status: "loading" };
      })
      .addCase(fetchVideoLinkPreview.fulfilled, (state, action: any) => {
        state.videoDetail = { data: action.payload, status: "succeeded" };
        state.videoId = action.payload?.videoId || "";
        state.videoSource = action.payload?.videoSource || "";
        state.hasProduct = action.payload?.hasProduct || false;
      })
      .addCase(fetchVideoLinkPreview.rejected, (state, action: any) => {
        state.videoDetail = { data: null, status: "failed", error: action.error?.message };
        message.error(action.error?.message || t("Video link not found, Please re-enter it."));
      });
    builder
      .addCase(fetchProductInfo.pending, (state) => {
        state.productItem = { ...state.productItem, status: "loading" };
      })
      .addCase(fetchProductInfo.fulfilled, (state, action) => {
        state.productItem = { data: action.payload, status: "succeeded" };
      })
      .addCase(fetchProductInfo.rejected, (state, action: any) => {
        const errorMessage = action.error?.message || t("Product not found, Please re-enter it.");
        state.productItem = { data: null, status: "failed", error: errorMessage };
        message.error(errorMessage);
      });
    builder
      .addCase(productSearchByUrl.pending, (state) => {
        state.productItem = { ...state.productItem, status: "loading" };
      })
      .addCase(productSearchByUrl.fulfilled, (state, action: any) => {
        state.productItem = { data: action.payload?.[0], status: "succeeded" };
        state.country = action.payload?.[0]?.country || "";
      })
      .addCase(productSearchByUrl.rejected, (state, action: any) => {
        const errorMessage = action.error?.message || t("Product not found, Please re-enter it.");
        state.productItem = { data: null, status: "failed", error: errorMessage };
        message.error(errorMessage);
      });
    builder
      .addCase(productSearch.pending, (state) => {
        state.productList = { ...state.productList, status: "loading" };
      })
      .addCase(productSearch.fulfilled, (state, action) => {
        state.productList = { data: (action.payload as any) || [], status: "succeeded" };
      })
      .addCase(productSearch.rejected, (state, action: any) => {
        state.productList = { data: [], status: "failed" };
        message.error(action.error?.message || t("Product not found, Please re-enter it."));
      });
    builder.addCase(fetchSellingPointsTask.pending, (state) => {
      state.sellingPoints = { ...state.sellingPoints, status: "loading" };
    });
    builder
      .addCase(fetchSellingPointsResult.pending, (state) => {
        state.sellingPoints = { ...state.sellingPoints, status: "loading" };
      })
      .addCase(fetchSellingPointsResult.fulfilled, (state, action) => {
        const data = (action.payload ?? []).map((item) => ({ checked: true, ...item }));
        state.sellingPoints = { data, status: "succeeded" };
        state.selling_points = data
          .map((item) => item.keyWord)
          .filter((keyWord): keyWord is string => Boolean(keyWord));
      })
      .addCase(fetchSellingPointsResult.rejected, (state, action: any) => {
        state.sellingPoints = { data: [], status: "failed" };
        message.error(action.error?.message || t("Selling points not found, Please re-enter it."));
      });
    builder.addCase(fetchScriptGenerationResult.fulfilled, (state, action) => {
      const { requestId, userInput } = action.meta.arg;
      const result: any[] = [];
      const payload = action.payload as any;
      Object.keys(payload).forEach((key) => {
        result.push({
          requestId,
          translate: false,
          video_scripts: payload[key],
          ...userInput,
        });
      });
      state.rewriteScriptList = [...state.rewriteScriptList, ...result];
    });
    builder
      .addCase(fetchScriptGenerationTask.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchScriptGenerationTask.fulfilled, (state, action) => {
        if (action.payload.code === 2) {
          // 任务完成，需要手动触发fetchVideoScripteMulation
          state.status = "succeeded";
          report.event("video_script_result", {
            kalo_type: "script_optimizer",
            kalo_value: "success",
          });
        } else if (action.payload.code === 3) {
          state.status = "failed";
          state.error = {
            code: "1305",
            message:
              "Loading failed, your credits have been refunded. Please try to refresh the page. If the problem persists, please contact us.",
          };
        } else if (action.payload.code === 4) {
          state.status = "failed";
          state.error = {
            code: "1306",
            message: "Not enough credits.",
          };
        } else if (action.payload.code === 5) {
          state.status = "failed";
          state.error = {
            code: "1307",
            message: "Sorry, You don't have permission to access this page.",
          };
        } else if (action.payload.code === 6) {
          state.status = "failed";
          state.error = {
            code: "1308",
            message: "Resource not ready.",
          };
        } else if (action.payload.code === 7) {
          state.status = "failed";
          state.error = {
            code: "1309",
            message: "Not support.",
          };
        }
      })
      .addCase(fetchScriptGenerationTask.rejected, (state, action: any) => {
        state.status = "failed";
        message.error(action.error?.message || t("Failed to generate script, Please try again."));
      });
    builder
      .addCase(fetchScriptGenerationResultTranslate.pending, (state) => {
        state.status = "loading";
        state.isTranslateLoading = true;
      })
      .addCase(fetchScriptGenerationResultTranslate.fulfilled, (state, action: any) => {
        // scriptIndex 是实际要更新的脚本索引，activeScript 是 API 参数（1 或 2）
        const { activeScript, scriptIndex } = action.meta.arg;
        const scriptKey = `script${activeScript}`;
        const newData = {
          ...state.rewriteScriptList[scriptIndex],
          requestId: state.rewriteScriptList[scriptIndex].requestId,
          video_scripts: action.payload?.[scriptKey],
          translate: true,
        };
        state.rewriteScriptList = state.rewriteScriptList.map((item: any, index: number) => {
          if (index === scriptIndex) {
            return newData;
          }
          return item;
        });
        state.status = "succeeded";
        state.isTranslateLoading = false;
      })
      .addCase(fetchScriptGenerationResultTranslate.rejected, (state) => {
        state.status = "failed";
        state.isTranslateLoading = false;
      });
  },
});

export const {
  resetToInit,
  updateReady,
  updateVideo,
  updateCountry,
  handleSave,
  updateProductItem,
  removeProductItem,
  updateStatus,
  updateError,
  updateTranslateStatus,
  removeSellingPoints,
  updateProductList,
  updateParams,
} = scriptRewriteSlice.actions;

const scriptRewriteReducer = scriptRewriteSlice.reducer;
export default scriptRewriteReducer;
